import React from 'react';
import clsx from 'clsx';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  iconPosition = 'left',
  className,
  disabled,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

  const sizeStyles = {
    sm: 'text-xs px-3 py-1.5 rounded-md gap-1.5 min-h-[36px]',
    md: 'text-sm px-4 py-2.5 rounded-lg gap-2 min-h-[44px]',
    lg: 'text-base px-5 py-3 rounded-xl gap-2.5 min-h-[48px]',
  };

  const variantStyles = {
    // Primary: WCAG AA 4.6:1 Pass for white text on primary-600
    primary: 'bg-primary-600 text-white hover:bg-primary-700 shadow-xs focus-visible:ring-primary-500 focus-visible:ring-offset-white',
    secondary: 'bg-slate-100 text-slate-900 hover:bg-slate-200 border border-slate-200/80 shadow-xs focus-visible:ring-slate-400',
    outline: 'bg-transparent text-slate-800 border border-slate-300 hover:bg-slate-50 focus-visible:ring-slate-400',
    ghost: 'bg-transparent text-slate-600 hover:text-primary-700 hover:bg-primary-50/60 focus-visible:ring-primary-400',
    danger: 'bg-red-600 text-white hover:bg-red-700 shadow-xs focus-visible:ring-red-500',
  };

  return (
    <button
      className={clsx(
        baseStyles,
        sizeStyles[size],
        variantStyles[variant],
        fullWidth && 'w-full',
        className
      )}
      disabled={disabled}
      {...props}
    >
      {icon && iconPosition === 'left' && <span className="shrink-0">{icon}</span>}
      <span>{children}</span>
      {icon && iconPosition === 'right' && <span className="shrink-0">{icon}</span>}
    </button>
  );
};
